// Dispatch — pure helpers. No React, no fetch, so every decision a screen
// makes about a label, a tone or a request body is pinned by dispatchFormat.test.js
// rather than trusted.
//
// Date-only strings ('YYYY-MM-DD', the DATE columns) are parsed at local noon
// through utils/scheduleDates.atNoon — never bare (dateOnlyBoundaries.test.js).

import { atNoon, addDaysISO } from '../../../utils/scheduleDates';
import { formatHours } from '../../../ui/fw';
import { formatDateInUserTZ, formatDateForInput } from '../../../utils/timezone';
import { STATUS_LABELS } from '../../shared/jobFields';
import { sameHours, clockLabel } from '../../shared/hoursPicker';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isDateOnly = (iso) => typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso);

/** 'YYYY-MM-DD' → 'Mon 8' — the time column of a sheet line, a board day. */
export function dayShort(iso) {
  if (!isDateOnly(iso)) return '';
  const d = atNoon(iso.slice(0, 10));
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()}`;
}

/** 'YYYY-MM-DD' → 'Tue 1 Sep' — the header eyebrow. */
export function dayLong(iso) {
  if (!isDateOnly(iso)) return '';
  const d = atNoon(iso.slice(0, 10));
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** 'YYYY-MM-DD' → 'Week of 8 Sep'. */
export function weekOfLabel(iso) {
  if (!isDateOnly(iso)) return '';
  const d = atNoon(iso.slice(0, 10));
  return `Week of ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** 'Today' / 'Tomorrow' / 'Tue 1 Sep', relative to the company's today. */
export function relativeDayLabel(iso, today) {
  if (!isDateOnly(iso)) return '';
  if (today && iso === today) return 'Today';
  if (today && iso === addDaysISO(today, 1)) return 'Tomorrow';
  return dayLong(iso);
}

/**
 * Where a multi-day job is in its own run: 'Day 2 of 3'. '' for a single day.
 *
 * ── Why the board needs this at all ─────────────────────────────────────────
 *
 * GET /api/dispatch/board returns a job under EVERY day its window covers
 * (server/routes/dispatch.js: `scheduled_date <= ? AND
 * COALESCE(scheduled_end_date, scheduled_date) >= ?`), which is right — a
 * three-day patio build is work on all three mornings. But the card drew
 * nothing to say so, so the same title appeared on today and on tomorrow
 * looking like two separate jobs, and a dispatcher had no way to tell a job
 * that is half done from one that has not started.
 *
 * The slot this fills used to hold `dayShort(day)` — the card's time column
 * repeating the date that is already the heading above it, on every card. A
 * fact slot spent on something the reader can already see is the em-dash
 * problem wearing a date.
 *
 * Dates are compared and stepped as STRINGS where possible and parsed at
 * local NOON when arithmetic is unavoidable (utils/scheduleDates.atNoon),
 * never at midnight — a zone whose DST shift lands on midnight has no such
 * instant and the Date silently moves to the adjacent day.
 *
 * Returns '' rather than a placeholder for every case it cannot answer: no
 * job, no start, no end, an end before the start, or a day outside the run.
 * A caller filters '' out of its fact list, so an unanswerable question costs
 * no slot at all.
 */
export function spanLabel(job, day) {
  if (!job || !isDateOnly(job.scheduled_date) || !isDateOnly(day)) return '';
  const start = job.scheduled_date.slice(0, 10);
  const end = isDateOnly(job.scheduled_end_date) ? job.scheduled_end_date.slice(0, 10) : start;
  if (end <= start) return '';                       // one day, or an end before its start
  const iso = day.slice(0, 10);
  if (iso < start || iso > end) return '';           // not in this run
  const days = (a, b) => Math.round((atNoon(a) - atNoon(b)) / 86400000);
  return `Day ${days(iso, start) + 1} of ${days(end, start) + 1}`;
}

/**
 * Decimal hours → 'H:MM' (8.25 → '8:15'). Display only.
 *
 * ── It TRUNCATED, and a second reading of the same thing ROUNDED ──────────
 *
 * This was `formatClock(Math.round(n * 3600))`, and formatClock floors the
 * minute — so 2.83 h, which is 2:49.8, read as **2:49**. The picker's own
 * `clockLabel` rounds to the nearest minute and read the same value as
 * **2:50**. Two readings of one number, on one screen, disagreeing on exactly
 * the row from the owner's report:
 *
 *   the grid, the week total, the change history   hoursClock(2.83) = 2:49
 *   the editor, and the "saving will set it to…"   clockLabel(2.83) = 2:50
 *
 * Found by review, and the parity test could not see it because it only drove
 * values the picker can produce — where both readings agree, because those
 * are whole minutes by construction. Legacy rows are the ones that are not,
 * and legacy rows are deliberately not migrated, so they persist.
 *
 * One implementation now, and it is the ROUNDING one: `hoursToParts` rounds
 * to the nearest step, and offGridNotice promises a person the figure it will
 * become. A grid that then showed a different minute would make that promise
 * false on screen.
 */
export const hoursClock = clockLabel;

/**
 * A server-computed money figure, as a person reads it — or null.
 *
 * FORMATTING. It rounds one number for display and computes nothing: every
 * figure it is handed is a return value of `computeFigures` in
 * server/utils/paystubCalc.js — the function that writes the paystub —
 * assembled by server/utils/timesheetGrid.js and sent down on the timesheet
 * read. Working a pay figure out on a SCREEN is #202's shape: a number
 * agreeing with neither the truth nor the paystub, with nothing on screen ever
 * contradicting anything (CLAUDE.md rule 4).
 *
 * `null` for anything that is not a finite number, and that is load-bearing
 * rather than defensive. An approver without `workers.read_sensitive` receives
 * `pay: null` on every figure in the grid; rendering that as "$0.00" would put
 * a WRONG number where a withheld one belongs, on the screen that turns hours
 * into pay. A caller handed null draws nothing at all.
 */
const MONEY_FMT = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export function moneyText(n) {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return MONEY_FMT.format(v);
}

/**
 * Elapsed seconds for an on-site row, ticking locally. The server's
 * `elapsed_seconds` is the base (it knows its own clock); the phone adds only
 * the time since the answer arrived, so a phone whose clock is wrong still
 * shows the right number.
 *
 * MOVED to apps/shared/liveClock.js on 2026-09-09, next to the request whose
 * two fields it reads: the Office jobs list now draws the same live clock on a
 * job row (docs/PARITY_2026-09-08.md §17) and a section may not import from
 * another section. Re-exported here so every dispatch caller and
 * dispatchFormat.test.js are untouched — the same move `serverSentence` made.
 */
export { elapsedNow } from '../../shared/liveClock';

/** An ISO timestamp → '7:42 AM' in the phone's zone. */
export function clockTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(d);
}

/**
 * A RAW `time_entries` timestamp → '3:26 PM', in the READER's zone.
 *
 * ── Why this is not clockTime() ────────────────────────────────────────────
 *
 * clockTime() is correct for what it is fed: GET /api/dispatch/now hands back
 * `started_at`, which server/routes/dispatch.js:99 has already normalised
 * through `parseStoredTimestamp(...).toISOString()` — a real ISO with a Z.
 *
 * GET /api/timesheets/:id/worker-jobs answers `te.*`, unprocessed. An
 * ordinary clock-in writes `start_time` with CURRENT_TIMESTAMP
 * (server/routes/timeEntries.js:268), so the value is the BARE SQLite form
 * '2026-09-09 19:26:53' — UTC, with no marker on it. Two separate mistakes
 * are available on that string and both are silent:
 *
 *   new Date('2026-09-09 19:26:53')   reads it as LOCAL rather than UTC
 *   Intl with no `timeZone`           renders it in the PROCESS zone
 *
 * Either is four or five hours out in Connecticut. utils/timezone
 * parseUTCDate is the one accessor in the client that reads both forms, and
 * getUserTimezone() is what makes the answer the reader's own wall clock
 * rather than the server's — which matters here because this is the evidence
 * an approver checks payroll hours against, and a session that reads 7:26 PM
 * when the crew worked 3:26 PM cannot be reconciled with anything.
 */
export function storedTime(at) {
  if (!at) return '';
  return formatDateInUserTZ(at, { hour: 'numeric', minute: '2-digit', hour12: true });
}

/**
 * The same timestamp → 'Wed 9', the day it fell on in the reader's zone.
 *
 * Goes through the zone FIRST and `dayShort` second, deliberately. 01:30 UTC
 * is still the previous evening in Connecticut, so a session bucketed by its
 * UTC date lands on a day the timesheet beside it does not agree with — and
 * an approver reconciling two lists that disagree about which day is which
 * has been given a discrepancy rather than an answer.
 */
export function storedDay(at) {
  const local = at ? formatDateForInput(at) : '';
  return local ? dayShort(local) : '';
}

/** Timesheet status → the pill on the approve list. */
export function sheetPill(timesheet) {
  const status = timesheet && timesheet.status;
  if (!timesheet) return { tone: 'warn', label: 'No sheet' };
  if (status === 'submitted') return { tone: 'now', label: 'To approve' };
  if (status === 'approved') return { tone: 'ok', label: 'Approved' };
  if (status === 'rejected') return { tone: 'stop', label: 'Rejected' };
  return { tone: 'neutral', label: 'Draft' };
}

/** 'Clock 41.25 · Sheet 41.25', or 'Clock 41.25 · No sheet'. */
export function hoursMeta(clockHours, timesheet) {
  const clock = `Clock ${formatHours(clockHours)}`;
  if (!timesheet) return `${clock} · No sheet`;
  return `${clock} · Sheet ${formatHours(timesheet.total_hours)}`;
}

/** The moves the board may make. Values are what the server accepts. */
export const MOVE_STATUSES = ['active', 'paused', 'completed'].map((value) => ({ value, label: STATUS_LABELS[value] || value }));

/**
 * Only what changed, for PATCH /api/jobs/:id/schedule. The server treats an
 * omitted field as "leave it", so sending the whole form would write an
 * activity line for every field on every move. `form.user_id` is '' for
 * unassigned; the server takes null.
 */
export function scheduleChanges(job, form) {
  const out = {};
  if (form.scheduled_date && form.scheduled_date !== (job.scheduled_date || null)) out.scheduled_date = form.scheduled_date;
  const lead = form.user_id === '' || form.user_id === null || form.user_id === undefined ? null : Number(form.user_id);
  if (lead !== (job.lead_user_id == null ? null : Number(job.lead_user_id))) out.user_id = lead;
  if (form.status && form.status !== job.status) out.status = form.status;
  const reason = (form.blocked_reason || '').trim();
  if (reason !== (job.blocked_reason || '')) out.blocked_reason = reason || null;
  return out;
}

/**
 * The full entries array for PUT /api/timesheets/:id. Lines not sent are
 * DELETED by the server, so every line goes, with its id; only the edited
 * line carries new hours and a reason. job_id and date ride along so the
 * server's duplicate-line check sees what it saw at creation.
 */
export function entriesForPut(entries, editedId, patch) {
  return (entries || []).map((e) => {
    const base = { id: e.id, job_id: e.job_id, date: e.date, hours: e.hours, adjustment_reason: e.adjustment_reason || null };
    if (editedId != null && e.id === editedId) {
      const next = { ...base, hours: patch.hours, adjustment_reason: patch.adjustment_reason || null };
      // ── An AMOUNT line's figure, when the caller is editing one ──────────
      //
      // Owner, 2026-09-18: "Can only fucking remove line items with fix
      // amounts, but not EDIT@!"
      //
      // True, and the gap was HERE rather than on the server: hoursModel.js:297
      // has read `e.pay_amount === undefined ? prev.pay_amount : ...` all along,
      // so a PUT may carry a new amount and an omitted one leaves the stored
      // figure alone. This function never put one on the wire, and EditLine had
      // no box for it, so a reimbursement or bonus line could be deleted and
      // re-typed and nothing else.
      //
      // `undefined` unless the caller passes one, deliberately and load-
      // bearingly: every other line in this array is sent untouched, and
      // sending `pay_amount: null` for them would WIPE the amount off every
      // amount line on the sheet on any edit.
      if (patch.pay_amount !== undefined) next.pay_amount = patch.pay_amount;
      // A RATE set on an hourly line (owner, 2026-09-28: "admin can override
      // at the timesheet") says its type too, so the line cannot be read as a
      // flat amount. Same rule: only when the caller passes one.
      if (patch.pay_type !== undefined) next.pay_type = patch.pay_type;
      return next;
    }
    return base;
  });
}

/**
 * Whether an edit needs a reason: hours differ from what the clock said and
 * there IS a clock. A manual line (no clock) never needs one — there is
 * nothing to differ from.
 */
export function reasonRequired(line, hours) {
  if (!line || line.clock_hours == null) return false;
  // To the MINUTE, via the one rule. This rounded both sides to the quarter
  // until 2026-09-16, so a five-minute change needed no reason AND was stored
  // as matching the clock — see sameHours in apps/shared/hoursPicker.js and
  // its mirror in server/utils/hoursModel.js, which carries the measurement.
  //
  // Deliberately still asks only about the CLOCK. A stricter version — "did
  // these hours move from what is stored" — was written and then removed: it
  // demanded a reason for setting a line BACK to what the clock recorded,
  // which is the one edit that needs no explaining. The problem it was
  // reaching for (a snap moving a figure nobody touched) is solved where it
  // belongs instead: nothing snaps unless a person picks something.
  return !sameHours(hours, line.clock_hours);
}


// serverSentence moved to apps/shared/http.js on 2026-09-03, next to the
// describeError it falls back to: the shared job panels need it too, and a
// shared module importing from one application is the coupling this rebuild
// keeps refusing elsewhere. Re-exported here so every existing caller and
// dispatchFormat.test.js are untouched.
export { serverSentence } from '../../shared/http';

/** Which tab a pathname under /dispatch lights up. */
export function tabFor(pathname) {
  const p = String(pathname || '');
  if (/^\/dispatch\/board(\/|$)/.test(p)) return 'board';
  if (/^\/dispatch\/approve(\/|$)/.test(p)) return 'approve';
  if (/^\/dispatch\/me(\/|$)/.test(p)) return null;
  return 'now';
}
